import { Route, Routes, Navigate, useNavigate, } from "react-router-dom";
import { useContext, useEffect } from "react";
import { ResponsiveContext, Grommet, Box, Header, Nav, Button, Text } from "grommet";
import Login from "../../components/UserManagement/Login";
import HomePage from "../../pages/HomePage";
import QueriesPage from "../../pages/QueriesPage";
import EvalManagerPage from "../../pages/EvalManagerPage";
import EvaluationSheetPage from "../../pages/EvaluationSheetPage";
import PreHarvestPage from "../../pages/PreHarvestPage";
import PostHarvestPage from "../../pages/PostHarvestPage";
import TreatmentPage from "../../pages/TreatmentPage";
import TemplateTreatmentsPage from "../../pages/TemplateTreatmentsPage";
import VineyardsManagementPage from "../../pages/VineyardsManagementPage";
import CultivarsManagementPage from "../../pages/CultivarsManagementPage";
import UserManagementPage from "../../pages/UserManagementPage";
import ResetPassword from "../../components/UserManagement/ResetPassword";
import { signOut } from "firebase/auth";
import { auth } from "../../components/UserManagement/firebase"; // 👈 your Firebase instance
import { UserContext } from "../../App"; // 👈 get user from context

const customTheme = {
    global: {
        breakpoints: {
            xsmall: { value: 375 },
            small: { value: 568, edgeSize: { none: "0px", small: "6px", medium: "12px", large: "24px" }},
            medium: { value: 768, edgeSize: { none: "0px", small: "12px", medium: "24px", large: "48px" }},
            large: { value: 1024, edgeSize: { none: "0px", small: "12px", medium: "24px", large: "48px" }},
            xlarge: { value: 1366, edgeSize: { none: "0px", small: "12px", medium: "24px", large: "48px" }},
        },
        deviceBreakpoints: { phone: 'small', tablet: 'medium', computer: 'large' },

        colors: {
            brand: '#00739D',
            text: "#00739D",
            border: "#00739D",
            selected: "#DADADA",
            select: "#00739D",
            input: "#00739D",
            label: "#00739D",
            icon: "#00739D",
            placeholder: "#9fbcc7", 
        },
        focus: {
            outline: {
                color: "#9fbcc7",
            },
        },        
        font: {
            family: 'Roboto, sans-serif',
        },
        hover: {
            border: {
                color: "#00739D",
                width: '56px',
            },
            color: "#02445c",
        },          
        
    },
    input: {
        color: "#00739D",   
    },
    textInput: {
        extend: `
        // border: none;
        // border-bottom: none;
        outline: none;
        border-radius: 0;
        padding: 4px 0;
        box-shadow: none;

                &:focus {
                    // border: none !important;
                    // border-bottom: none !important;
                    outline: none !important;
                    box-shadow: none !important;
                }

                &:active {
                    // border: none !important;
                    // outline: none !important;
                    box-shadow: none !important;
                    border-bottom: none !important;
                }
        `,
    },    
    calendar: {
        day: {
            extend: ({ isSelected }) => `
                ${isSelected && `
                    background-color: #DADADA;
                    color: #02445c;
                  //  border-radius: 50%;
                `}
            `,
        },
        header: { // This is the header of the calendar
            // level: 4, // Adjust the heading level if needed
            // weight: 600, // Optional: Adjust the font weight
            extend: `
                background-color: #00739D !important; /* Use !important to override any default styles */
                color: white;
                padding: 10px; // Adjust the padding for spacing
                text-align: center;
            `,
        },
    }, 
    span: {
        color: "#00739D",
    }, 
    checkBox: {
        border: {
            color: "#00739D",
        }
    },
    select: {    
        // Styles for each INDIVIDUAL option in the dropdown
        options: {
            color: '#00739D',
            container: {               
                extend: ({ selected }) => `
                padding: 0px !important;
                ${selected ? `
                    background-color: #ebf4f7;
                ` : ''}
                `,
            },
            text: {
                color: '#00739D',
            },
        },
        listbox: {
            container: {
              extend: `
                padding: 0px !important;
              `,
            },
          },      
        icons: {
            color: "#00739D",
        },
    },        
    button: {
        default: {
            extend: () => `
                color: white;
            //     background-color: #CCCCCC;
            //     border: none;
            //     padding: 8px 16px;
            //     border-radius: 4px;
            `,
        },
        primary: {
            color: "white",
            background: "#00739D",        
        },
        secondary: {
            extend: () => `
                color: #00739D;
                background-color: 'light-2';
                border: 2px solid #00739D;
            //   padding: 8px 16px;
            //   border-radius: 4px;
            `,
        },
    },     
};

const MainRouter = () => {
    const currentUser = useContext(UserContext);
    const isAuthenticated = !!currentUser;
    // console.log('In MainRouter currentUser >> ', currentUser);

    // Check if the user is an admin.
    const isAdmin = currentUser?.isAdmin === true; 

    const navigate = useNavigate();

    // Optional: simplify display name from email
    const displayName =
        currentUser?.name || currentUser?.email?.split("@")[0] || "Guest";

    // Optional: redirect to login if trying to access protected route
    useEffect(() => {
        const unprotectedRoutes = ["/", "/reset-password"];
        const currentPath = window.location.pathname;

        if (!isAuthenticated && !unprotectedRoutes.includes(currentPath)) {
        navigate("/login"); // 👈 make sure you have a /login route
        }
    }, [isAuthenticated, navigate]);

    const handleLogout = async () => {
        await signOut(auth);
        navigate("/home");
    };

    const buttonStyles = {
        pad: { vertical: "none" },
        margin: { horizontal: "medium" },
        primary: true,
        color: "#76b9cf",
    };

    const calculateHeight = (percent) => `${percent}vh`;

    return (
        <Grommet theme={customTheme} full background="#f5f5f5">
                <ResponsiveContext.Consumer>
                    {(screenSize) => {
                        const logoSize = {
                            xsmall: "40px",
                            small: "50px",
                            medium: "60px",
                            large: "70px",
                            xlarge: "80px"
                        }[screenSize] || "60px";

                        return (
                            <Box flex fill style={{ position: 'fixed' }}>
                                {/* Header */}
                                <Box
                                    direction="row"
                                    justify="end"
                                    width="92%"
                                    height={calculateHeight(5)}
                                    alignSelf="center"
                                    background="dark-2"
                                    pad="small"
                                >
                                    <Header direction="row" justify="end" height="100%">
                                        <Nav direction="row" gap="medium">
                                        {!isAuthenticated ? (
                                        <Button
                                            {...buttonStyles}
                                            label="Log in"
                                            onClick={() => navigate("/login")}
                                        />
                                        ) : (
                                        <Box direction="row" justify="around">
                                            <Text
                                            size="medium"
                                            alignSelf="center"
                                            weight="bold"
                                            color="#EEEEEE"
                                            >
                                            Hello, {displayName}
                                            </Text>
                                            <Button
                                            {...buttonStyles}
                                            label="Log out"
                                            onClick={handleLogout}
                                            />
                                        </Box>
                                        )}
                                        </Nav>
                                    </Header>
                                </Box>

                                {/* Main Content */}
                                <Box
                                    align="center"
                                    width="92%"
                                    background="#f5f5f5"
                                    height="87vh"
                                    margin="0 auto"
                                >
                                    <Box width="100%" height="100%" background="white" elevation="small" overflow="auto">
                                        <Routes>
                                            <Route path="/" element={<HomePage />} />
                                            <Route path="/login" element={<Login />} />
                                            <Route path="/reset-password" element={<ResetPassword />} />
                         
                                            {isAuthenticated ? (
                                                <>
                                                    <Route 
                                                        path="/cult-manager" 
                                                        element={isAdmin ? <CultivarsManagementPage /> : <Navigate to="/eval-manager" />} 
                                                    />                                                    
                                                    <Route 
                                                        path="/fields-manager" 
                                                        element={isAdmin ? <VineyardsManagementPage /> : <Navigate to="/eval-manager" />} 
                                                    />
                                                    <Route 
                                                        path="/user-manager" 
                                                        element={isAdmin ? <UserManagementPage /> : <Navigate to="/eval-manager" />} 
                                                    />
                                                    <Route 
                                                        path="/st-treatment" 
                                                        element={isAdmin ? <TemplateTreatmentsPage /> : <Navigate to="/eval-manager" />} 
                                                    />
                                                    <Route path="/queries" element={<QueriesPage />} />
                                                    <Route path="/eval-manager" element={<EvalManagerPage />} />
                                                    <Route path="/eval-sheet" element={<EvaluationSheetPage />} />
                                                    <Route path="/preharvest" element={<PreHarvestPage />} />
                                                    <Route path="/postharvest" element={<PostHarvestPage />} />
                                                    <Route path="/treatment" element={<TreatmentPage />} />
                                                    <Route path="*" element={<Navigate to="/" />} />
                                                </>
                                            ) : (
                                                <>
                                                    <Route path="/cult-manager" element={<Navigate to="/login" />} />
                                                    <Route path="/fields-manager" element={<Navigate to="/login" />} />
                                                    <Route path="/user-manager" element={<Navigate to="/login" />} />
                                                    <Route path="/st-treatment" element={<Navigate to="/login" />} />
                                                    <Route path="/queries" element={<Navigate to="/login" />} />
                                                    <Route path="/eval-manager" element={<Navigate to="/login" />} />
                                                    <Route path="/eval-sheet" element={<Navigate to="/login" />} />
                                                    <Route path="/preharvest" element={<Navigate to="/login" />} />
                                                    <Route path="/postharvest" element={<Navigate to="/login" />} />
                                                    <Route path="/treatment" element={<Navigate to="/login" />} />
                                                    <Route path="*" element={<Navigate to="/" />} />
                                                </>
                                            )}
                                        </Routes>
                                    </Box>
                                </Box>

                                {/* Footer */}
                                <Box
                                    direction="row"
                                    gap="medium"
                                    width="92%"
                                    justify="center"
                                    background="white"
                                    height="12vh"
                                    pad={{ horizontal: "medium", vertical: "small" }}
                                    margin="0 auto"
                                >
                                    <Box
                                        height="100%"
                                        width={logoSize}
                                        background={{
                                            image: "url('Volcani_logo.png')",
                                            size: "contain",
                                            repeat: "no-repeat",
                                            position: "center",
                                        }}
                                    />
                                    <Box
                                        height="100%"
                                        width="60%"
                                        justify="center"
                                        background={{
                                            image: "url('Title.JPG')",
                                            size: "contain",
                                            repeat: "no-repeat",
                                            position: "left center",
                                        }}
                                    />
                                </Box>
                                {/*end of Footer*/}
                            </Box>
                        );
                    }}
                </ResponsiveContext.Consumer>
        </Grommet>
    );
};

export default MainRouter;
